/**
 * @fileoverview Quick Progress Update Section Component
 *
 * Allows admin to quickly update:
 * - Construction Phase (with auto-progress calculation)
 * - Project Status
 *
 * Features:
 * - Edit/Save/Cancel functionality
 * - Auto-calculated progress bar based on phase
 * - Status badge with color coding
 *
 * @version 1.0.0
 */

import React, { useState, useEffect } from "react";
import { FaHourglassHalf, FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { formatProgress } from "../../../../utils/projectUtils";
import { constructionPhaseSummaries } from "../../../../utils/constructionPhaseSummaries";
import { PhaseIcon } from "../../../../components/icons/phaseicons";

const WARRANTY_STATUS = "Construction Complete / Under Warranty";

/**
 * Project status options for editing
 * @constant {Array<Object>}
 */
const PROJECT_STATUSES = [
  { value: "Pre-Construction", label: "Pre-Construction" },
  { value: "Active Construction", label: "Active Construction" },
  { value: "Construction Delayed", label: "Construction Delayed" },
  {
    value: "Construction Complete / Under Warranty",
    label: "Under Warranty",
  },
];

/**
 * Construction phases with order and progress values
 * @constant {Array<Object>}
 */
const CONSTRUCTION_PHASES = [
  { value: "In Development", label: "In Development", progress: 0 },
  { value: "Planning/Permitting", label: "Planning/Permitting", progress: 5 },
  { value: "Foundation", label: "Foundation", progress: 15 },
  { value: "Framing", label: "Framing", progress: 30 },
  { value: "Rough-In", label: "Rough-In", progress: 45 },
  {
    value: "Insulation & Drywall",
    label: "Insulation & Drywall",
    progress: 55,
  },
  { value: "Trim & Paint", label: "Trim & Paint", progress: 70 },
  { value: "Mechanical Trim", label: "Mechanical Trim", progress: 80 },
  { value: "Final Finishes", label: "Final Finishes", progress: 90 },
  { value: "Punch List", label: "Punch List", progress: 95 },
  {
    value: "Final Inspection / CO",
    label: "Final Inspection / CO",
    progress: 98,
  },
  { value: "Complete", label: "Complete", progress: 100 },
];

/**
 * Get status badge color class
 * @param {string} status - Status value
 * @returns {string} CSS class name
 */
const getStatusColor = (status) => {
  switch (status) {
    case "Active Construction":
      return "status-active";
    case "Pre-Construction":
      return "status-pre";
    case "Construction Delayed":
      return "status-delayed";
    case "Construction Complete / Under Warranty":
      return "status-warranty";
    default:
      return "status-default";
  }
};

/**
 * Section wrapper component with consistent styling
 */
const Section = ({ title, icon, children, action }) => (
  <div className="apd-section">
    <div className="apd-section-header">
      <div className="apd-section-title">
        {icon && <span className="apd-section-icon">{icon}</span>}
        <h2>{title}</h2>
      </div>
      {action && <div className="apd-section-action">{action}</div>}
    </div>
    <div className="apd-section-content">{children}</div>
  </div>
);

/**
 * QuickProgressUpdate Component
 * Editable phase and status with auto-calculated progress bar
 *
 * @param {Object} props - Component props
 * @param {string} props.projectId - The project ID
 * @param {Object} props.project - Project data object
 * @param {Function} props.onUpdate - Callback when update is saved
 * @param {boolean} props.isUpdating - Loading state during save
 */
const QuickProgressUpdate = ({
  projectId,
  project,
  onUpdate,
  isUpdating = false,
}) => {
  // Local state for editing
  const [isEditingProgress, setIsEditingProgress] = useState(false);
  const [editedPhase, setEditedPhase] = useState("");
  const [editedStatus, setEditedStatus] = useState("");
  const [editedActualCompletion, setEditedActualCompletion] = useState("");

  // Initialize edited values when project changes
  useEffect(() => {
    if (project) {
      setEditedPhase(project.constructionPhase || "");
      setEditedStatus(project.projectStatus || "");
      setEditedActualCompletion(project.actualCompletion || "");
    }
  }, [project]);

  /**
   * Handle status change - auto-manage actual completion date
   */
  const handleStatusChange = (newStatus) => {
    setEditedStatus(newStatus);
    if (newStatus === WARRANTY_STATUS) {
      if (!editedActualCompletion) {
        setEditedActualCompletion(new Date().toISOString().slice(0, 10));
      }
    } else {
      setEditedActualCompletion("");
    }
  };

  /**
   * Handle phase change - update progress automatically
   */
  const handlePhaseChange = (newPhase) => {
    setEditedPhase(newPhase);
  };

  /**
   * Handle save button click
   */
  const handleSaveProgress = async () => {
    if (onUpdate) {
      await onUpdate({
        constructionPhase: editedPhase,
        projectStatus: editedStatus,
        actualCompletion:
          editedStatus === WARRANTY_STATUS ? editedActualCompletion : "",
      });
    }
    setIsEditingProgress(false);
  };

  /**
   * Handle cancel button click
   */
  const handleCancelEdit = () => {
    setEditedPhase(project?.constructionPhase || "");
    setEditedStatus(project?.projectStatus || "");
    setEditedActualCompletion(project?.actualCompletion || "");
    setIsEditingProgress(false);
  };

  if (!project) {
    return (
      <Section title="Progress Update" icon={<FaHourglassHalf />}>
        <div className="apd-loading-small">Loading progress data...</div>
      </Section>
    );
  }

  return (
    <Section
      title="Progress Update"
      icon={<FaHourglassHalf />}
      action={
        !isEditingProgress ? (
          <button
            className="apd-edit-btn"
            onClick={() => setIsEditingProgress(true)}
          >
            <FaEdit />
            <span className="hide-sm"> Edit</span>
          </button>
        ) : (
          <div className="apd-edit-actions">
            <button
              className="apd-save-btn"
              onClick={handleSaveProgress}
              disabled={isUpdating}
            >
              <FaSave /> <span className="hide-sm"> Save</span>
            </button>
            <button
              className="apd-cancel-btn"
              onClick={handleCancelEdit}
              disabled={isUpdating}
            >
              <FaTimes /> <span className="hide-sm"> Cancel</span>
            </button>
          </div>
        )
      }
    >
      <div className="apd-progress-section">
        {isEditingProgress ? (
          /* Edit Mode: labeled dropdowns */
          <div className="apd-progress-edit-grid">
            <div className="apd-progress-field">
              <label>Construction Phase</label>
              <select
                value={editedPhase}
                onChange={(e) => handlePhaseChange(e.target.value)}
                className="apd-field-select"
              >
                {CONSTRUCTION_PHASES.map((phase) => (
                  <option key={phase.value} value={phase.value}>
                    {phase.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="apd-progress-field">
              <label>Project Status</label>
              <select
                value={editedStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="apd-field-select"
              >
                {PROJECT_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            {editedStatus === WARRANTY_STATUS && (
              <div className="apd-progress-field">
                <label>Actual Completion Date</label>
                <input
                  type="date"
                  className="apd-field-select"
                  value={editedActualCompletion}
                  onChange={(e) => setEditedActualCompletion(e.target.value)}
                />
              </div>
            )}
          </div>
        ) : (
          /* Read Mode: phase icon + name + status badge, then summary */
          <div className="apd-progress-read">
            <div className="apd-progress-read-header">
              <div className="apd-progress-read-phase">
                <PhaseIcon
                  phase={project.constructionPhase}
                  size={36}
                  className="apd-phase-icon"
                />
                <span className="apd-field-value apd-phase-value">
                  {project.constructionPhase}
                </span>
              </div>
              <span
                className={`apd-field-status-badge ${getStatusColor(
                  project.projectStatus,
                )}`}
              >
                {project.projectStatus}
              </span>
            </div>
            <p className="apd-progress-summary">
              {
                constructionPhaseSummaries[project.constructionPhase]
                  .fullSummary
              }
            </p>
          </div>
        )}

        {/* Read-only Progress Bar */}
        <div className="apd-progress-bar-section">
          <div className="apd-progress-bar-header">
            <span className="apd-progress-label">Overall Progress</span>
            <span className="apd-progress-value">
              {formatProgress(project.projectProgress)}%
            </span>
          </div>
          <div className="apd-progress-bar">
            <div
              className="apd-progress-fill"
              style={{ width: `${formatProgress(project.projectProgress)}%` }}
            />
          </div>
          <p className="apd-progress-note">
            Progress is automatically calculated based on construction phase
          </p>
        </div>
      </div>
    </Section>
  );
};

export default QuickProgressUpdate;
